import type { ProjectExecute } from "../types/ead-types.ts";
import {
  findLatestTemplatePfmTreeRun,
  type PfmLineageRunRef,
} from "./pfm-version-label.ts";

/**
 * Runs eligible to serve as the source for PFM mindmap + node-report inheritance.
 * Matches gateway rules: not marked invalid for reporting, and still contributes to learning.
 * Completion status is not required.
 */
export function isExecutionEligibleForPfmInheritance(run: ProjectExecute | undefined | null): boolean {
  if (!run?.id) return false;
  if (run.validForDataReportingTraining === false) return false;
  if (run.contributesToLearning === false) return false;
  return true;
}

/** True when this run has its own accepted PFM delivery (not inherited preview only). */
export function runHasOwnDeliveredPfmMap(run: ProjectExecute | undefined | null): boolean {
  return run?.hasDeliveredPfmMapReport === true;
}

/** Whether this run's explore reports feed the persistent product knowledge base. */
export function isExecutionValidForInteractiveLearning(
  run: ProjectExecute | undefined | null,
): boolean {
  return isExecutionEligibleForPfmInheritance(run) && runHasOwnDeliveredPfmMap(run);
}

/**
 * Show the continuous-learning (brain) control after login succeeds for this run.
 */
export function shouldShowContinuousLearningToggle(
  run: ProjectExecute | undefined | null,
  options?: { loginSuccessful?: boolean },
): boolean {
  const loginOk = options?.loginSuccessful ?? true;
  return Boolean(loginOk && run?.id);
}

export function sortExecutionsByStartTimeDesc(a: ProjectExecute, b: ProjectExecute): number {
  return (b.startTime ?? 0) - (a.startTime ?? 0);
}

export function sortExecutionsByRunNumberDesc(a: ProjectExecute, b: ProjectExecute): number {
  return (b.runNumber ?? 0) - (a.runNumber ?? 0);
}

/** Most recent eligible run for the template (by `runNumber`), or null. */
export function latestPfmInheritanceSourceForTemplate(
  templateId: string | undefined | null,
  executions: ProjectExecute[],
): ProjectExecute | null {
  if (!templateId?.trim()) return null;
  const eligible = executions
    .filter((e) => e.linkedTemplateId === templateId && isExecutionEligibleForPfmInheritance(e))
    .sort(sortExecutionsByRunNumberDesc);
  return eligible[0] ?? null;
}

const _TERMINAL_RUN_STATUSES = new Set([
  "completed",
  "failed",
  "cancelled",
  "error",
  "abandoned",
]);

function runHasCommittedPfmTreeForDeepLearning(run: ProjectExecute): boolean {
  if (!isExecutionEligibleForPfmInheritance(run)) return false;
  const st = String(run.status || "").trim().toLowerCase();
  if (!_TERMINAL_RUN_STATUSES.has(st)) return false;
  if (run.pfmHasCommittedSnapshot === true) return true;
  if (Math.max(0, Number(run.pfmRevision || 0)) > 0) return true;
  if (Math.max(0, Number(run.pfmGenerationVersion || 0)) > 0 && st === "completed") {
    return true;
  }
  return false;
}

/** True when the template already has a finished run with a DB-committed PFM tree. */
export function templateAllowsNodeDeepLearning(
  templateId: string,
  executions: ProjectExecute[],
): boolean {
  const tid = String(templateId || "").trim();
  if (!tid) return false;
  const peers = executions.filter((e) => String(e.linkedTemplateId || "").trim() === tid);
  if (findLatestTemplatePfmTreeRun(peers as PfmLineageRunRef[], "")) return true;
  return peers.some((run) => runHasCommittedPfmTreeForDeepLearning(run));
}
